import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Pagination from '@mui/material/Pagination';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import Collapse from '@mui/material/Collapse';
import { createLead, updateLead, getProjects, fetchLeads, publishLeadsToMarketplace, fetchClientStatuses } from '../api';
import { isSales } from '../auth';
import { LeadForm } from './LeadForm';
import { LeadsTable } from './LeadsTable';
import { PublishMarketplaceModal } from './PublishMarketplaceModal';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

export const LeadsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [leads, setLeads] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [projectId, setProjectId] = React.useState('');
  const [clientStatus, setClientStatus] = React.useState('');
  const [clientStatuses, setClientStatuses] = React.useState<string[]>([]);
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [sentDateFrom, setSentDateFrom] = React.useState('');
  const [sentDateTo, setSentDateTo] = React.useState('');
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingLead, setEditingLead] = React.useState<any>(null);
  const [projects, setProjects] = React.useState<any[]>([]);
  const [activeTab, setActiveTab] = React.useState(0);
  const [showFilters, setShowFilters] = React.useState(!isMobile);
  
  // Состояние пагинации
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [totalCount, setTotalCount] = React.useState(0);

  // Sync showFilters with isMobile
  React.useEffect(() => {
    setShowFilters(!isMobile);
  }, [isMobile]);
  
  // Используем useRef для предотвращения дублирования запросов
  const isFetchingLeads = React.useRef(false);
  const isFetchingProjects = React.useRef(false);
  const projectsLoaded = React.useRef(false);

  // Notifications (Snackbar top-right)
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [notifMessage, setNotifMessage] = React.useState('');
  const [notifSeverity, setNotifSeverity] = React.useState<'success' | 'error' | 'warning' | 'info'>('info');
  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setNotifMessage(message);
    setNotifSeverity(severity);
    setNotifOpen(true);
  };

  // Confirmation dialog
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmTitle, setConfirmTitle] = React.useState('');
  const [confirmDescription, setConfirmDescription] = React.useState<string | undefined>('');
  const [confirmOkText, setConfirmOkText] = React.useState('OK');
  const [confirmCancelText, setConfirmCancelText] = React.useState('Cancel');
  const confirmResolver = React.useRef<((val: boolean) => void) | null>(null);
  const requestConfirm = React.useCallback((opts: { title: string; description?: string; confirmText?: string; cancelText?: string; }) => {
    return new Promise<boolean>((resolve) => {
      setConfirmTitle(opts.title);
      setConfirmDescription(opts.description);
      setConfirmOkText(opts.confirmText || (t('confirm') as string) || 'Подтвердить');
      setConfirmCancelText(opts.cancelText || (t('cancel') as string) || 'Отмена');
      confirmResolver.current = resolve;
      setConfirmOpen(true);
    });
  }, [t]);

  const handleConfirmClose = (accepted: boolean) => {
    setConfirmOpen(false);
    if (confirmResolver.current) {
      confirmResolver.current(accepted);
      confirmResolver.current = null;
    }
  };

  // Marketplace modal
  const [marketplaceOpen, setMarketplaceOpen] = React.useState(false);
  const [marketplaceLeadIds, setMarketplaceLeadIds] = React.useState<number[]>([]);

  const handlePublishToMarketplace = async (leadIds: number[], options: {
    region?: string;
    city?: string;
    niche?: string;
    price: number;
  }) => {
    try {
      const result = await publishLeadsToMarketplace(leadIds, options);
      showNotification(
        `Опубликовано: ${result.imported}, дубликатов: ${result.duplicates}`,
        result.imported > 0 ? 'success' : 'warning',
      );
    } catch (err: any) {
      showNotification(err.message || 'Ошибка публикации на маркетплейс', 'error');
      throw err;
    }
  };

  // Загружаем проекты только один раз при монтировании компонента
  React.useEffect(() => {
    if (!projectsLoaded.current) {
      projectsLoaded.current = true;
      isFetchingProjects.current = true;

      getProjects()
        .then(data => {
          setProjects(data);
          isFetchingProjects.current = false;
        })
        .catch(err => {
          console.error('Ошибка загрузки проектов:', err);
          isFetchingProjects.current = false;
        });
    }
  }, []);

  React.useEffect(() => {
    fetchClientStatuses()
      .then(setClientStatuses)
      .catch(err => console.error('Ошибка загрузки статусов AMO:', err));
  }, []);

  // Функция для загрузки лидов с фильтрами и пагинацией
  const fetchLeadsWithFilters = React.useCallback(() => {
    if (isFetchingLeads.current) return;
    
    isFetchingLeads.current = true;
    setLoading(true);
    
    fetchLeads({
      page,
      limit: pageSize,
      status,
      phone,
      projectId,
      clientStatus,
      dateFrom,
      dateTo,
      sentDateFrom,
      sentDateTo,
      activeTab
    })
      .then(data => { 
        // Предполагаем, что API возвращает { data: [], total: number }
        if (data.data && data.total !== undefined) {
          setLeads(data.data);
          setTotalCount(data.total);
        } else {
          // Fallback для старого формата API
          setLeads(data);
          setTotalCount(data.length);
        }
        setLoading(false); 
        isFetchingLeads.current = false;
      })
      .catch((err: any) => { 
        setError(err.message); 
        setLoading(false); 
        isFetchingLeads.current = false;
      });
  }, [activeTab, status, phone, projectId, clientStatus, dateFrom, dateTo, sentDateFrom, sentDateTo, page, pageSize]);

  // Загружаем лиды при изменении фильтров, вкладки или пагинации
  React.useEffect(() => {
    fetchLeadsWithFilters();
  }, [fetchLeadsWithFilters]);

  // Сбрасываем страницу при изменении фильтров или вкладки
  React.useEffect(() => {
    setPage(1);
  }, [activeTab, status, phone, projectId, clientStatus, dateFrom, dateTo, sentDateFrom, sentDateTo]);

  const handleCreate = async (data: any) => {
    try {
      await createLead(data);
      setDialogOpen(false);
      // Обновляем список лидов
      fetchLeadsWithFilters();
      showNotification(t('lead_created') || 'Лид создан', 'success');
    } catch (err: any) {
      setError(err.message);
      showNotification(t('error_creating_lead') || 'Ошибка при создании лида', 'error');
    }
  };

  const handleEdit = async (data: any) => {
    try {
      await updateLead(editingLead.id, data);
      setDialogOpen(false);
      setEditingLead(null);
      // Обновляем список лидов
      fetchLeadsWithFilters();
      showNotification(t('lead_updated') || 'Лид обновлен', 'success');
    } catch (err: any) {
      setError(err.message);
      showNotification(t('error_updating_lead') || 'Ошибка при обновлении лида', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    // Этот обработчик теперь используется для пометки лида как брак
    // Лид помечается как invalidLead: true и исчезает из текущего списка
    // Обновляем список лидов после пометки как брак
    fetchLeadsWithFilters();
  };

  const handleEditLead = (lead: any) => {
    setEditingLead(lead);
    setDialogOpen(true);
  };

  const handleUpdateLead = (leadId: number, updates: any) => {
    setLeads(prevLeads => 
      prevLeads.map(lead => 
        lead.id === leadId ? { ...lead, ...updates } : lead
      )
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Фильтры сохраняются при смене вкладки для удобства пользователя
  };

  const handleManualSearch = () => {
    // Принудительно загружаем лиды заново
    fetchLeadsWithFilters();
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(event.target.value);
    setPage(1); // Сбрасываем на первую страницу при изменении размера
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        mb: 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', gap: 1, flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
          {!isSales() && (
          <Button
            variant="contained"
            color="primary"
            fullWidth={isMobile}
            onClick={() => { setEditingLead(null); setDialogOpen(true); }}
          >
            Добавить лид
          </Button>
          )}
        </Box>
        {isMobile && (
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            fullWidth
          >
            {showFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
          </Button>
        )}
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="Вкладки лидов"
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
        >
          <Tab label="Все" />
          <Tab label="Горячие" />
          <Tab label="ЛНБ" />
          <Tab label="Отправленные" />
          <Tab label="Брак" />
        </Tabs>
      </Box>
      
      {/* Фильтры для всех вкладок */}
      <Collapse in={showFilters}>
        <Box 
          mb={2} 
          display="flex" 
          gap={1.5} 
          alignItems="center" 
          flexWrap="wrap"
          sx={{
            backgroundColor: isMobile ? '#f8f9fa' : 'transparent',
            p: isMobile ? 2 : 0,
            borderRadius: 2,
            border: isMobile ? '1px solid #eee' : 'none'
          }}
        >
          <input
            placeholder="Телефон"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            style={{ 
              padding: '8px 12px', 
              minWidth: isMobile ? '100%' : 120,
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
          <select 
            value={status} 
            onChange={e => setStatus(e.target.value)} 
            style={{ 
              padding: '8px 12px', 
              minWidth: isMobile ? '100%' : 150,
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: 'white'
            }}
          >
            <option value="">{t('status') || 'Статус'}</option>
            <option value="UNPROCESSED">Необработанный</option>
            <option value="AT_WORK">В работе</option>
            <option value="COMPLETED">Завершен</option>
            <option value="NON_CALL">Недозвон</option>
            <option value="INVALID">Невалидный лид</option>
            <option value="BONUS">Бонусный</option>
          </select>
          <Autocomplete
            options={projects}
            getOptionLabel={option => option.name}
            value={projects.find(p => String(p.id) === String(projectId)) || null}
            onChange={(event, newValue) => setProjectId(newValue ? String(newValue.id) : '')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label="Проект" placeholder="Найти проект..." size="small" />
            )}
            sx={{ minWidth: isMobile ? '100%' : 180, backgroundColor: 'white' }}
          />
          <Autocomplete
            options={clientStatuses}
            value={clientStatus || null}
            onChange={(event, newValue) => setClientStatus(newValue || '')}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <TextField {...params} label="Статус АМО" placeholder="пришел, не пришел..." size="small" />
            )}
            sx={{ minWidth: isMobile ? '100%' : 180, backgroundColor: 'white' }}
          />

          <Box display="flex" gap={1} alignItems="center">
            <Typography variant="body2" sx={{ mr: 1 }}>Создан:</Typography>
            <input
              type="date"
              placeholder="С даты"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              style={{ padding: 8 }}
            />
            <input
              type="date"
              placeholder="До даты"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              style={{ padding: 8 }}
            />
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Typography variant="body2" sx={{ mr: 1 }}>Отправлен:</Typography>
            <input
              type="date"
              placeholder="С даты"
              value={sentDateFrom}
              onChange={e => setSentDateFrom(e.target.value)}
              style={{ padding: 8 }}
            />
            <input
              type="date"
              placeholder="До даты"
              value={sentDateTo}
              onChange={e => setSentDateTo(e.target.value)}
              style={{ padding: 8 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto' }}>
            <Button 
              variant="contained" 
              onClick={handleManualSearch} 
              sx={{ flex: isMobile ? 1 : 'none' }}
            >
              {t('search') || 'Поиск'}
            </Button>
            <Button 
              variant="text" 
              onClick={() => {
                setPhone('');
                setStatus('');
                setProjectId('');
                setDateFrom('');
                setDateTo('');
              }}
              sx={{ flex: isMobile ? 1 : 'none' }}
            >
              Сбросить
            </Button>
          </Box>
        </Box>
      </Collapse>
      
      {loading ? (
        <Typography>{t('loading') || 'Loading...'}</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <LeadsTable
            leads={leads}
            onEdit={isSales() ? undefined : handleEditLead}
            onDelete={isSales() ? undefined : handleDelete}
            onUpdate={isSales() ? undefined : handleUpdateLead}
            onSent={fetchLeadsWithFilters}
            onNotify={(msg, severity) => showNotification(msg, severity)}
            onConfirm={(opts) => requestConfirm({
              title: opts.title,
              description: opts.description,
              confirmText: opts.confirmText,
              cancelText: opts.cancelText,
            })}
            onPublishToMarketplace={isSales() ? undefined : (lead) => {
              setMarketplaceLeadIds([lead.id]);
              setMarketplaceOpen(true);
            }}
          />
          
          {/* Пагинация */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mt: 2, 
            p: isMobile ? 1 : 2,
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            borderTop: '1px solid #e0e0e0'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              width: isMobile ? '100%' : 'auto',
              justifyContent: isMobile ? 'center' : 'flex-start'
            }}>
              <Typography variant="body2" color="text.secondary">
                Показано {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} из {totalCount}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  displayEmpty
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              showFirstButton={!isMobile}
              showLastButton={!isMobile}
              color="primary"
              size={isMobile ? "small" : "large"}
            />
          </Box>
        </>
      )}

      {/* Snackbar notification top-right */}
      <Snackbar
        open={notifOpen}
        autoHideDuration={4000}
        onClose={() => setNotifOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert onClose={() => setNotifOpen(false)} severity={notifSeverity} elevation={6} variant="filled">
          {notifMessage}
        </MuiAlert>
      </Snackbar>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => handleConfirmClose(false)}>
        <DialogTitle>{confirmTitle}</DialogTitle>
        {confirmDescription && (
          <DialogContent>
            <Typography variant="body1">{confirmDescription}</Typography>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => handleConfirmClose(false)}>{confirmCancelText}</Button>
          <Button variant="contained" color="primary" onClick={() => handleConfirmClose(true)}>
            {confirmOkText}
          </Button>
        </DialogActions>
      </Dialog>

      <LeadForm
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditingLead(null); }}
        lead={editingLead}
        onSubmit={editingLead ? handleEdit : handleCreate}
      />

      <PublishMarketplaceModal
        open={marketplaceOpen}
        onClose={() => setMarketplaceOpen(false)}
        leadIds={marketplaceLeadIds}
        onPublish={handlePublishToMarketplace}
      />
    </>
  );
}; 